/**
 * Screen Auth Callback — V11 OAuth 登录回调处理（通用）
 * 
 * 路由: #/auth-callback?token=<jwt>&openid=<openid>&provider=<github|google|wechat>&name=<name>&avatar=<url>
 * 
 * 职责:
 *   - 解析 URL query 中的 JWT token 和用户信息
 *   - 保存 token + 用户资料到 localStorage
 *   - 重定向到首页
 * 
 * 来源: 各 OAuth 授权后 → /api/auth/{provider}/callback → 302 重定向到此路由
 */

import { setAuthSession } from "../services/llm-service";

const PENDING_AUTH_PATH_KEY = "battlecode-pending-auth-path";

export function renderAuthCallback(
  app: HTMLElement,
  onNavigate: (path: string) => void,
): void {
  app.innerHTML = `
    <div class="s0-root">
      <div class="auth-callback-center">
        <div class="auth-callback-spinner"></div>
        <p class="auth-callback-text">正在验证登录...</p>
      </div>
    </div>
  `;

  // 从 URL hash 中解析 query 参数
  // 格式: #/auth-callback?token=xxx&openid=yyy&provider=github&name=Alice&avatar=...
  const hash = window.location.hash;
  const queryIdx = hash.indexOf("?");
  if (queryIdx === -1) {
    setTimeout(() => onNavigate("#/"), 1000);
    return;
  }

  const queryStr = hash.slice(queryIdx + 1);
  const params = new URLSearchParams(queryStr);
  const token = params.get("token");
  const openid = params.get("openid");
  const provider = params.get("provider") || "unknown";
  const name = params.get("name") || "";
  const avatar = params.get("avatar") || "";

  if (token && openid) {
    void setAuthSession(token, { openid, provider, name, avatar }).then(() => {
      console.info(`[auth-callback] login success · ${provider} · ${name || openid.slice(0, 8)}…`);

      app.innerHTML = `
        <div class="s0-root">
          <div class="auth-callback-center">
            <div class="auth-callback-icon auth-callback-icon--success">&#x2713;</div>
            <p class="auth-callback-text">登录成功</p>
            <p class="auth-callback-sub">${name ? `欢迎, ${name}` : "正在返回首页..."}</p>
          </div>
        </div>
      `;

      // 清除 hash 中的敏感参数；若登录前有未完成的 Forge 操作，则自动返回铸剑阁继续执行。
      const pendingPath = localStorage.getItem(PENDING_AUTH_PATH_KEY);
      if (pendingPath) localStorage.removeItem(PENDING_AUTH_PATH_KEY);
      setTimeout(() => {
        onNavigate(pendingPath || "#/");
      }, 800);
    }).catch((error: unknown) => {
      console.error("[auth-callback] failed to initialize user context", error);
      app.innerHTML = `
        <div class="s0-root">
          <div class="auth-callback-center">
            <div class="auth-callback-icon auth-callback-icon--fail">&#x2717;</div>
            <p class="auth-callback-text">登录失败</p>
            <p class="auth-callback-sub">无法初始化用户数据，请重试。</p>
          </div>
        </div>
      `;
    });
  } else {
    console.warn("[auth-callback] no token in URL");

    app.innerHTML = `
      <div class="s0-root">
        <div class="auth-callback-center">
          <div class="auth-callback-icon auth-callback-icon--fail">&#x2717;</div>
          <p class="auth-callback-text">登录失败</p>
          <p class="auth-callback-sub">缺少鉴权令牌，正在返回首页...</p>
        </div>
      </div>
    `;

    setTimeout(() => onNavigate("#/"), 1500);
  }
}

export function cleanupAuthCallback(_app: HTMLElement): void {
  // auth-callback 无持久资源需清理（setTimeout 会自动执行）
}
